import { isoStringToDate } from "../shared/date-format";
import { ColumnDefinition, EntityInterface } from "./Table";

/**
 * A Transaction Event is a transaction that has been sent to NLS. Suppliers can perform operations on
 * an Authorization (capture, void, etc). These operations can affect the status and amount of the
 * authorization and are performed in NLS.
 * 
 * One Authorization can have many associated Transaction Events
 */

/** Transaction Event statuses */
export type TransactionEventStatus = 'captured' | 'voided' | 'refunded';

/**
 * Interface for a transaction event
 */
export interface TransactionEventInterface {
    accepted_tx_id: string;          // Related authorization (accepted transaction)
    amount: number;                  // Transaction amount
    buyer_id: string;                // Buyer's company ID
    created_at: string | null;       // Date of creation
    id: string;                      // Unique identifier for the transaction event
    last_updated_at: string | null;  // Date of last update (null if no updates)
    status: TransactionEventStatus;  // Transaction status
    supplier_id: string;             // Supplier's company ID
    tx_request_id: string;           // Related transaction request
    memo: string;                    // Transaction memo
    remaining_amount: number;     // Remaining amount after this transaction event
    acctrefno?: string;               // Optional account reference number
}

/**
 * TransactionEvent class representing a transaction event
 */
export class TransactionEvent implements EntityInterface {
    /**
     * Constructor for the TransactionEvent class
     * 
     * @param accepted_tx_id Related authorization (accepted transaction)
     * @param amount Transaction amount
     * @param buyer_id Buyer's company ID
     * @param created_at Date of creation
     * @param id Unique identifier for the transaction event
     * @param last_updated_at Date of last update (null if no updates)
     * @param status Transaction status
     * @param supplier_id Supplier's company ID
     * @param tx_request_id Related transaction request
     * @param memo Transaction memo
     */
    constructor(
        public accepted_tx_id: string,
        public amount: number,
        public buyer_id: string,
        public created_at: Date | null,
        public id: string,
        public last_updated_at: Date | null,
        public status: TransactionEventStatus,
        public supplier_id: string,
        public tx_request_id: string,
        public memo: string,
        public remaining_amount: number,
        public acctrefno?: string,
    ) { }

    /**
     * Creates a copy of the current TransactionEvent instance
     * 
     * @returns A copy of the current TransactionEvent instance
     */
    copyOf(): TransactionEvent {
        return new TransactionEvent(
            this.accepted_tx_id,
            this.amount,
            this.buyer_id,
            this.created_at,
            this.id,
            this.last_updated_at,
            this.status,
            this.supplier_id,
            this.tx_request_id,
            this.memo,
            this.remaining_amount,
            this.acctrefno
        );
    }

    /**
     * Return true if the current TransactionEvent instance is equal to the given transaction
     * 
     * @param other 
     * @returns 
     */
    equals(other: TransactionEvent): boolean {
        return this.accepted_tx_id === other.accepted_tx_id &&
            this.amount === other.amount &&
            this.buyer_id === other.buyer_id &&
            this.created_at === other.created_at &&
            this.id === other.id &&
            this.last_updated_at === other.last_updated_at &&
            this.status === other.status &&
            this.supplier_id === other.supplier_id &&
            this.tx_request_id === other.tx_request_id &&
            this.memo === other.memo &&
            this.remaining_amount === other.remaining_amount &&
            this.acctrefno === other.acctrefno;
    }

    /**
     * Returns a string representation of the TransactionEvent instance for filtering
     * 
     * @param defs The column definitions to use for formatting
     * @returns A string representation of the TransactionEvent instance
     */
    toString(defs: ColumnDefinition<any>[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }

    /**
     * Gets the status as a titlecase string
     * 
     * @returns The status in titlecase format
     */
    get statusTitleCase(): string {
        return this.status.charAt(0).toUpperCase() + this.status.slice(1);
    }
}

/** Mapper for converting between TransactionEventInterface and TransactionEvent */
export class TransactionEventMapper {
    static toDto(transaction: TransactionEvent): TransactionEventInterface {
        return {
            accepted_tx_id: transaction.accepted_tx_id,
            amount: transaction.amount,
            buyer_id: transaction.buyer_id,
            created_at: transaction.created_at?.toISOString() || null,
            id: transaction.id,
            last_updated_at: transaction.last_updated_at?.toISOString() || null,
            status: transaction.status,
            supplier_id: transaction.supplier_id,
            tx_request_id: transaction.tx_request_id,
            memo: transaction.memo || '',
            remaining_amount: transaction.remaining_amount,
            acctrefno: transaction.acctrefno,
        };
    }

    static toModel(dto: TransactionEventInterface): TransactionEvent {
        return new TransactionEvent(
            dto.accepted_tx_id,
            dto.amount,
            dto.buyer_id,
            isoStringToDate(dto.created_at),
            dto.id,
            isoStringToDate(dto.last_updated_at),
            dto.status,
            dto.supplier_id,
            dto.tx_request_id,
            dto.memo,
            dto.remaining_amount,
            dto.acctrefno
        );
    }
}
